"use client";
import { useCallback, useState, useMemo } from "react";
import Image from "next/image";
import styles from "./index.module.scss";

import { ReportParentContentEnum } from "@/app/types/typeConfig/report";
import { IMConfigTypeEnum } from "@/app/types/typeConfig/chatQuestion";
import { useTranslations } from "@/app/lib/LocaleProvider";
import { useMessageStore } from "@/app/store";
import useAIBehaviorCollect from "@/app/hooks/robot/useAIBehaviorCollect";
import { createUuid, copyToClipboard } from "@/app/utils/string";
import {
  HeaderType,
  MessageSender,
  MessageType,
  RobotMessageType,
} from "@/app/store/types";
import { Toast } from "@douyinfe/semi-ui";
/**
 * AI 消息底部栏组件属性
 */
interface TechAIFooterProps {
  uniqueKey?: string;
  data: {
    parentContentInfo?: {
      parentContentId?: string;
      parentContent?: string;
      parentContentType?: ReportParentContentEnum;
    };
    [key: string]: unknown;
  };
  aiTypingEnd?: boolean; // AI SSE是否还在推消息
  error?: boolean; // 是否处于错误状态
  showSubmitTicket?: boolean;
  artificialStop?: boolean;
  showTechAIFooter?: boolean;
  requestId?: string;
  content?: string; // 消息内容，用于复制
  onStop?: () => void;
  onRedo?: () => void;
  onLike?: () => void;
  onDislike?: () => void;
}

/**
 * 反馈状态类型
 */
type FeedbackState = "none" | "liked" | "disliked";

/**
 * AI 消息底部栏组件
 * 提供 AI 回复的状态提示和用户反馈功能（点赞/点踩/停止）
 */
export default function TechAIFooter({
  data,
  error = false,
  showSubmitTicket = true,
  showTechAIFooter = true,
  requestId,
  content,
  aiTypingEnd,
  onStop,
  onRedo,
  onLike,
  onDislike,
}: TechAIFooterProps) {
  const { setRobotMessage } = useMessageStore();
  const { onLikeCollect, onDislikeCollect } = useAIBehaviorCollect();
  const t = useTranslations();
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("none");

  // 计算图标 URL
  const iconUrls = useMemo(
    () => ({
      like:
        feedbackState === "liked"
          ? "/images/like2-visited.svg"
          : "/images/like2.svg",
      dislike:
        feedbackState === "disliked"
          ? "/images/dislike2-visited.svg"
          : "/images/dislike2.svg",
    }),
    [feedbackState],
  );

  const isError = useMemo(() => {
    console.log("Error status:", error);
    return error;
  }, [error]);

  // 处理点踩
  const handleDislike = useCallback(() => {
    if (feedbackState !== "none") return;
    setFeedbackState("disliked");
    onDislikeCollect(
      {
        parentContentId:
          (data.parentContentInfo?.parentContentId as string) || "",
        parentContent: (data.parentContentInfo?.parentContent as string) || "",
        parentContentType:
          (data.parentContentInfo
            ?.parentContentType as ReportParentContentEnum) ||
          ReportParentContentEnum.DEFAULT,
      },
      requestId,
    );
    setRobotMessage({
      content: "",
      timestamp: Date.now(),
      streaming: false,
      clientId: createUuid(),
      type: MessageType.Text,
      headerType: HeaderType.MESSAGE,
      senderType: MessageSender.ROBOT,
      messageType: RobotMessageType.ROBOT_FEEDBACK,
      senderName: "智能助手",
      robotData: {
        feedback: "dislike",
        requestId: requestId,
        parentContentInfo: data.parentContentInfo,
        configType: IMConfigTypeEnum.techAiFAQ,
      },
    });
    onDislike?.();
  }, [feedbackState, onDislike, requestId]);

  // 处理点赞
  const handleLike = useCallback(() => {
    if (feedbackState !== "none") return;
    setFeedbackState("liked");
    onLikeCollect(
      {
        parentContentId:
          (data.parentContentInfo?.parentContentId as string) || "",
        parentContent: (data.parentContentInfo?.parentContent as string) || "",
        parentContentType:
          (data.parentContentInfo
            ?.parentContentType as ReportParentContentEnum) ||
          ReportParentContentEnum.DEFAULT,
      },
      requestId,
    );
    setRobotMessage({
      content: "",
      timestamp: Date.now(),
      streaming: false,
      clientId: createUuid(),
      type: MessageType.Text,
      headerType: HeaderType.MESSAGE,
      senderType: MessageSender.ROBOT,
      messageType: RobotMessageType.ROBOT_FEEDBACK,
      senderName: "智能助手",
      robotData: {
        feedback: "like",
        requestId: requestId,
        parentContentInfo: data.parentContentInfo,
        configType: IMConfigTypeEnum.techAiFAQ,
      },
    });

    onLike?.();
  }, [feedbackState, onLike, requestId]);

  // 处理停止
  const handleStop = useCallback(() => {
    onStop?.();
  }, [onStop]);

  // 处理重做
  const handleRedo = useCallback(() => {
    onRedo?.();
  }, [onRedo]);

  // 处理复制
  const handleCopy = useCallback(async () => {
    if (!content) return;
    // 将 HTML 内容放到临时 div 中，提取纯文本
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    if (!textContent.trim()) return;
    const success = await copyToClipboard(textContent);
    if (success) {
      Toast.success(t.tips.copySuccess);
    }
  }, [content, t.tips.copySuccess]);

  // 获取反馈按钮的 className
  const getFeedbackClassName = useCallback(
    (type: "like" | "dislike") => {
      const baseClass = type === "like" ? styles.like : styles.dislike;
      if (feedbackState === "none") return baseClass;

      const isActive =
        (type === "like" && feedbackState === "liked") ||
        (type === "dislike" && feedbackState === "disliked");

      return `${baseClass} ${isActive ? styles.active : styles.inactivated}`;
    },
    [feedbackState],
  );

  // 不显示底部栏
  if (!showTechAIFooter) return null;
  return (
    <div className={styles.tips}>
      {/* 状态文本区域 */}
      <div className={styles.statusText}>
        {isError ? (
          // 暂停状态：显示暂停提示和重做按钮
          <div className={styles.suspendedWrapper}>
            <span className={styles.red}>{t.chat.AISuspended}</span>
            <button
              className={styles.redoWrapper}
              onClick={handleRedo}
              aria-label="重新生成"
            >
              <Image src="/images/redo.svg" height={20} width={20} alt="" />
            </button>
          </div>
        ) : (
          // 正常状态
          <span>{t.chat.AIProvided}</span>
        )}
      </div>

      {/* 操作按钮区域 */}
      {showSubmitTicket && (
        <div className={styles.iconWrapper}>
          {
            // 打字完成或错误/暂停状态显示复制/点赞/点踩按钮
            !isError && (
              <>
                {content && aiTypingEnd && (
                  <div
                    className={styles.copy}
                    onClick={handleCopy}
                    role="button"
                    tabIndex={0}
                    aria-label={t.chat.copy}
                  >
                    <Image
                      src="/images/copy.svg"
                      height={20}
                      width={20}
                      alt={t.chat.copy}
                    />
                  </div>
                )}
                <div
                  className={getFeedbackClassName("like")}
                  onClick={feedbackState === "none" ? handleLike : undefined}
                  role="button"
                  tabIndex={0}
                  aria-label="点赞"
                  style={{
                    cursor:
                      feedbackState === "none" ? "pointer" : "not-allowed",
                  }}
                >
                  <Image
                    src={iconUrls.like}
                    height={20}
                    width={20}
                    alt="点赞"
                  />
                </div>
                <div
                  className={getFeedbackClassName("dislike")}
                  onClick={feedbackState === "none" ? handleDislike : undefined}
                  role="button"
                  tabIndex={0}
                  aria-label="点踩"
                  style={{
                    cursor:
                      feedbackState === "none" ? "pointer" : "not-allowed",
                  }}
                >
                  <Image
                    src={iconUrls.dislike}
                    height={20}
                    width={20}
                    alt="点踩"
                  />
                </div>
              </>
            )
          }
        </div>
      )}
    </div>
  );
}
